import { ReactNode, useCallback } from "react";
import { DateTime } from "luxon";
import Tooltip from "@mui/material/Tooltip";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import i18n from "../../i18n";
import { RootState } from "../../state/store";
import { useOnlineStatus } from "./useOnlineStatus";

type ShiftExistFlags = { day: boolean; afternoon: boolean; night: boolean };

export function monthOf(d: DateTime) {
  return new Date(d.year, d.month - 1, d.day);
}

function monthBounds(d: Date) {
  const monthStart = DateTime.fromJSDate(d).startOf("month");
  const fromIso = monthStart.minus({ days: 7 }).toISODate()!;
  const toIso = monthStart.endOf("month").plus({ days: 7 }).toISODate()!;
  const key = monthStart.toFormat("yyyy-LL");
  return { key, fromIso, toIso };
}

export type ShiftCalendar = {
  isDateAllowed: (date: Date) => boolean;
  isShiftDate: (iso: string) => boolean;
  renderDayContents: (day: number, date?: Date) => ReactNode;
  isFetching: boolean;
};

export const useShiftCalendar = (viewedMonth: Date, enabled: boolean): ShiftCalendar => {
  const isOnline = useOnlineStatus();
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate);
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const { key, fromIso, toIso } = monthBounds(viewedMonth);

  const { data: byDate = {}, isFetching } = useQuery({
    queryKey: ["shiftExistence", key],
    queryFn: async () => {
      const res = await settingsAjax.getShiftExistence(fromIso, toIso);
      if (res instanceof ErrorResponse) throw new Error("Failed to fetch shift existence");

      const result: Record<string, ShiftExistFlags> = {};
      res.days.forEach(d => {
        const iso = d.date.toISODate();
        if (!iso) return;
        result[iso] = { day: d.day, afternoon: d.afternoon, night: d.night };
      });
      return result;
    },
    enabled,
    staleTime: 15 * 1000,
    gcTime: 30 * 1000
  });

  const isShiftDate = useCallback(
    (iso: string) => {
      if (!isOnline && (iso === provisionalShiftDate || iso === latestShiftDate)) return true;

      const flags = byDate[iso];
      // Note: the afternoon shift counts. A date whose only shift was the afternoon one was greyed out
      // as having no shifts at all.
      if (flags) return flags.day || flags.afternoon || flags.night;

      return false;
    },
    [byDate, isOnline, provisionalShiftDate, latestShiftDate]
  );

  const isDateAllowed = useCallback((date: Date) => isShiftDate(DateTime.fromJSDate(date).toISODate()!), [isShiftDate]);

  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    if (isDateAllowed(date)) return <span>{day}</span>;

    return (
      <Tooltip
        title={isOnline
          ? i18n.t("shiftCalendar.noShifts")
          : i18n.t("shiftCalendar.offline")}
        arrow
        placement="top"
      >
        <div className="text-gray-200 cursor-not-allowed h-full w-full">{day}</div>
      </Tooltip>
    );
  };

  return { isDateAllowed, isShiftDate, renderDayContents, isFetching };
};
